import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { playSoundEffect } from '../hooks/useSoundEffects';
import { getEffectiveSoundOutputVolume } from '../hooks/soundEffectsManager';
import { getTutorialVoicePath, stopTutorialVoice } from './tutorialVoiceMap';
import { getFactionIconPath } from '../utils/factionHudDisplay';
import { MENU_PAINTED_SURFACE_FILTER, WELCOME_PANEL_BACKGROUND_PATH } from '../config/welcomeAssets';
import { resolvePublicAssetBackgroundUrl } from '../utils/resolvePublicAssetUrl';
import MenuPaintedButton from '../components/MenuPaintedButton';
import './TutorialMessageBox.css';

const PREFERENCE_FACTION_MAP = {
  aggressive: 'The Corrupted Core',
  greedy: 'The Covenant',
  patient: 'The Last Crusade',
  controlling: 'The Forsaken Pact',
  creative: 'The Shadow Consortium',
};

const FACTION_ORB_BACKGROUNDS = {
  'The Corrupted Core': 'linear-gradient(to right, #4B0082 50%, #8B0000 50%)',
  'The Covenant': 'linear-gradient(to right, #F4A460 50%, #1A1A1A 50%)',
  'The Last Crusade': 'linear-gradient(to right, #F4A460 50%, #556B2F 50%)',
  'The Forsaken Pact': 'linear-gradient(to right, #556B2F 50%, #8B0000 50%)',
  'The Shadow Consortium': 'linear-gradient(to right, #1A1A1A 50%, #4B0082 50%)',
};

/**
 * Reusable tutorial message box with image, text, optional multichoice selector, and buttons.
 * @param {Object} props
 * @param {string} props.image - Path to image (e.g. /images/tutorial_welcome.png)
 * @param {string} props.text - Main text content
 * @param {string} [props.goldText] - Optional paragraph rendered in gold below the main text
 * @param {Array<{key: string, value: string}>} [props.choices] - Optional multichoice options
 * @param {string} [props.choiceLabelPrefix] - Translation key prefix for choice labels (e.g. 'battleOverview.tutorial'). Default: 'welcome.strategyPreference'
 * @param {Function} [props.onSelect] - Called when user selects a choice (for strategy selection)
 * @param {Function} props.onContinue - Called when user clicks Continue
 * @param {Function} [props.onCancel] - Called when user clicks Cancel
 * @param {boolean} [props.showCancel] - Whether to show Cancel button
 * @param {boolean} [props.disabled] - Disable choice buttons (e.g. while submitting)
 * @param {string} [props.voiceKey] - Translation key for tutorial voice (e.g. 'welcome.tutorial.welcome'). Voice stops when dialog is closed.
 * @param {string[]} [props.voiceKeys] - Optional array of voice keys to play in sequence in the same dialog (e.g. intro then gold). Overrides voiceKey when set.
 * @param {string} [props.wrapperClassName] - Optional extra class on the root `.tutorial-wrapper` (e.g. page-specific z-index).
 * @param {string} [props.choiceButtonImagePath] - Optional painted button asset for choice rows (e.g. green_button4).
 * @param {string} [props.choiceButtonClassName] - Optional extra class for painted choice buttons.
 * @param {string} [props.cancelButtonImagePath] - Optional painted button asset for cancel (e.g. gray_button2).
 * @param {string} [props.continueButtonImagePath] - Optional painted button asset for continue/close.
 * @param {string} [props.continueButtonClassName] - Optional extra class for painted continue button.
 * @param {string} [props.continueLabelKey] - Translation key for continue/close label. Default: welcome.tutorial.continue
 * @param {string} [props.paintedButtonSurfaceFilter] - Optional CSS filter for painted choice/cancel buttons.
 */
function TutorialMessageBox({
  image,
  text,
  goldText,
  choices,
  choiceLabelPrefix = 'welcome.strategyPreference',
  onSelect,
  onContinue,
  onCancel,
  showCancel = false,
  disabled = false,
  voiceKey,
  voiceKeys,
  wrapperClassName,
  choiceButtonImagePath,
  choiceButtonClassName = '',
  cancelButtonImagePath,
  continueButtonImagePath,
  continueButtonClassName = '',
  continueLabelKey = 'welcome.tutorial.continue',
  paintedButtonSurfaceFilter = MENU_PAINTED_SURFACE_FILTER,
}) {
  const { t } = useTranslation();
  const voiceAudioRef = useRef(null);
  const pathsToPlayRef = useRef([]);
  const nextIndexRef = useRef(0);

  useEffect(() => {
    const keys = Array.isArray(voiceKeys) && voiceKeys.length > 0
      ? voiceKeys
      : (voiceKey ? [voiceKey] : []);
    const paths = keys.map(k => getTutorialVoicePath(k)).filter(Boolean);
    stopTutorialVoice(voiceAudioRef);
    voiceAudioRef.current = null;
    pathsToPlayRef.current = paths;
    nextIndexRef.current = 0;

    if (paths.length === 0) return;

    function playNext() {
      if (nextIndexRef.current >= pathsToPlayRef.current.length) return;
      const volume = getEffectiveSoundOutputVolume(1);
      if (volume === null) return;
      const path = pathsToPlayRef.current[nextIndexRef.current];
      nextIndexRef.current += 1;
      try {
        const audio = new Audio(path);
        audio.volume = volume;
        voiceAudioRef.current = audio;
        audio.addEventListener('ended', () => {
          playNext();
        });
        audio.play().catch(() => {});
      } catch (_) {}
    }

    playNext();

    return () => {
      stopTutorialVoice(voiceAudioRef);
      voiceAudioRef.current = null;
      pathsToPlayRef.current = [];
    };
  }, [voiceKey, voiceKeys]);

  return (
    <div
      className={`tutorial-wrapper${wrapperClassName ? ` ${wrapperClassName}` : ''}`}
      style={{
        '--tutorial-message-panel-background-image': resolvePublicAssetBackgroundUrl(WELCOME_PANEL_BACKGROUND_PATH),
        '--tutorial-message-panel-surface-filter': MENU_PAINTED_SURFACE_FILTER,
      }}
    >
      <div className="tutorial-message-box">
        {image && (
          <div className="tutorial-image-column">
            <div className="tutorial-image-container">
              <img src={image} alt="" className="tutorial-image" />
            </div>
          </div>
        )}
        <div className="tutorial-content-column">
          <div className="tutorial-text">
            {text}
            {goldText && <p className="tutorial-text-gold">{goldText}</p>}
          </div>
          {choices && choices.length > 0 && (
            <div className="tutorial-choices">
              {choices.map(({ key, value }) => {
                const factionName = PREFERENCE_FACTION_MAP[key];
                const orbBg = factionName ? FACTION_ORB_BACKGROUNDS[factionName] : null;
                const factionIconPath = factionName ? getFactionIconPath(factionName) : null;
                const choiceLabel = t(`${choiceLabelPrefix}.${key}`);
                const choiceContent = (
                  <>
                    {orbBg && (
                      <span
                        className="tutorial-choice-orb"
                        style={{ background: orbBg }}
                        title={factionName}
                      >
                        {factionIconPath && (
                          <img
                            src={factionIconPath}
                            alt=""
                            className="tutorial-choice-orb-icon"
                            draggable={false}
                            aria-hidden="true"
                          />
                        )}
                      </span>
                    )}
                    <span className="tutorial-choice-label">{choiceLabel}</span>
                  </>
                );

                if (choiceButtonImagePath) {
                  return (
                    <MenuPaintedButton
                      key={key}
                      className={`tutorial-choice-button tutorial-choice-button--painted${choiceButtonClassName ? ` ${choiceButtonClassName}` : ''}`}
                      imagePath={choiceButtonImagePath}
                      surfaceFilter={paintedButtonSurfaceFilter}
                      onClick={() => {
                        if (!disabled && onSelect) {
                          onSelect(value);
                        }
                      }}
                      disabled={disabled}
                    >
                      {choiceContent}
                    </MenuPaintedButton>
                  );
                }

                return (
                  <button
                    key={key}
                    className="tutorial-choice-button"
                    onClick={() => {
                      if (!disabled && onSelect) {
                        playSoundEffect('/sounds/click.mp3');
                        onSelect(value);
                      }
                    }}
                    disabled={disabled}
                  >
                    {choiceContent}
                  </button>
                );
              })}
            </div>
          )}
          <div className="tutorial-buttons">
            {!choices?.length && (
              continueButtonImagePath ? (
                <MenuPaintedButton
                  className={`tutorial-continue-button${continueButtonClassName ? ` ${continueButtonClassName}` : ' tutorial-continue-button--painted'}`}
                  imagePath={continueButtonImagePath}
                  surfaceFilter={paintedButtonSurfaceFilter}
                  onClick={() => {
                    onContinue();
                  }}
                >
                  {t(continueLabelKey)}
                </MenuPaintedButton>
              ) : (
                <button
                  className="tutorial-continue-button"
                  onClick={() => {
                    playSoundEffect('/sounds/click.mp3');
                    onContinue();
                  }}
                >
                  {t(continueLabelKey)}
                </button>
              )
            )}
            {showCancel && (
              cancelButtonImagePath ? (
                <MenuPaintedButton
                  className="tutorial-cancel-button tutorial-cancel-button--painted"
                  imagePath={cancelButtonImagePath}
                  surfaceFilter={paintedButtonSurfaceFilter}
                  onClick={() => {
                    if (onCancel) onCancel();
                  }}
                >
                  {t('common.cancel')}
                </MenuPaintedButton>
              ) : (
                <button
                  className="tutorial-cancel-button"
                  onClick={() => {
                    playSoundEffect('/sounds/click.mp3');
                    if (onCancel) onCancel();
                  }}
                >
                  {t('common.cancel')}
                </button>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TutorialMessageBox;
